/**
 * Site usage → BuzzBrain app: practice at buzzbrain.ai — its filter bar and its
 * tabs, the same six as the science site's (AppUsageTabs.tsx).
 *
 * Overview, Tables' sources and What students did are built from per-person
 * day rows, which are complete — every answer and hint is recorded server-side
 * for every student. Screens, Flow, All landings and Paths come from recorded
 * sessions, which exist only while session tracing is on; each says how many
 * students it covers. See docs/FeatureDesigns/acquisition/site-usage-target.md §6.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchFilterBar, TabStrip, type FilterDef } from '@shared-ui/components';
import SiteFlowChart from '../shared/SiteFlowChart';
import { appUsageApi, APP_FILTER_KEYS, type AppUsage } from '../../../api/appUsageApi';
import { firstTouchLabel } from '../shared/sourceLabels';
import { Landings, Overview, Paths, Tables, WhatStudentsDid } from './AppUsageTabs';
import { n } from './format';

const PLATFORM_LABEL: Record<string, string> = { 'web-student': 'Web', 'web-teacher': 'Teacher web', ios: 'iOS', android: 'Android' };

/** The person filters, each option with its count of people. Values that are
 *  not in the window are not offered. */
function filterDefs(data?: AppUsage): FilterDef[] {
  const o = data?.filterOptions;
  const opts = (key: keyof NonNullable<typeof o>, label: (v: string) => string = v => v) =>
    (o?.[key] ?? []).map(x => ({ value: x.value, label: `${label(x.value)} (${n(x.people)})` }));
  return [
    { key: 'who', kind: 'select', placeholder: 'Everyone', options: [
      { value: 'guest', label: 'Guests' }, { value: 'account', label: 'With an account' },
    ] },
    { key: 'source', kind: 'select', placeholder: 'Any first source', options: opts('source', firstTouchLabel) },
    { key: 'campaign', kind: 'select', placeholder: 'Any campaign', options: opts('campaign') },
    { key: 'link', kind: 'select', placeholder: 'Any link', options: opts('link') },
    { key: 'returning', kind: 'select', placeholder: 'New & returning', options: [
      { value: 'new', label: 'New — account made in this window' },
      { value: 'returning', label: 'Returning — account made before' },
    ] },
    { key: 'platform', kind: 'select', placeholder: 'Any platform', options: opts('platform', p => PLATFORM_LABEL[p] ?? p) },
  ];
}

type AppTab = 'overview' | 'tables' | 'did' | 'flow' | 'landings' | 'paths';
/** The science site's six, in its order; the third counts students, not
 *  sessions, so it says so. */
const APP_TABS = [
  { key: 'overview' as const, label: 'Overview' },
  { key: 'tables' as const, label: 'Tables' },
  { key: 'did' as const, label: 'What students did' },
  { key: 'flow' as const, label: 'Flow' },
  { key: 'landings' as const, label: 'All landings' },
  { key: 'paths' as const, label: 'Paths' },
];

export default function AppUsageReport({ from, to, filters, setFilters }: {
  from: Date; to: Date;
  filters: Record<string, string>;
  setFilters: (v: Record<string, string>) => void;
}) {
  // Only this report's keys go to its endpoint, and only they key its cache:
  // a science-site host filter must not refetch, or narrow, this report.
  const own = useMemo(
    () => Object.fromEntries(APP_FILTER_KEYS.filter(k => filters[k]).map(k => [k, filters[k]])),
    [filters],
  );
  const { data, isLoading, error } = useQuery({
    queryKey: ['app-usage', from.toISOString(), to.toISOString(), own],
    queryFn: () => appUsageApi.get(from, to, own),
  });
  const defs = useMemo(() => filterDefs(data), [data]);
  const [tab, setTab] = useState<AppTab>('overview');

  return (
    <>
      <SearchFilterBar filters={defs} values={filters} onChange={setFilters} hideSearch hideFetch />
      <TabStrip tabs={APP_TABS} active={tab} onChange={setTab} ariaLabel="BuzzBrain app views" className="sa-tabs" />
      {error && <p className="sa-error">{(error as Error).message}</p>}
      {isLoading && <p className="sa-empty">Loading…</p>}
      {tab === 'flow' && <SiteFlowChart from={from} to={to} scope={own} site="app" />}
      {data && tab !== 'flow' && (
        <>
          {tab === 'overview' && <Overview data={data} />}
          {tab === 'tables' && <Tables data={data} />}
          {tab === 'did' && <WhatStudentsDid data={data} />}
          {tab === 'landings' && <Landings data={data} />}
          {tab === 'paths' && <Paths data={data} />}
          {(data.excludedAccounts > 0 || data.scienceReaders > 0) && (
            <p className="sa-note">
              Left out: {n(data.scienceReaders)} science-site {data.scienceReaders === 1 ? 'reader' : 'readers'} whose
              guest account opened nothing in the app, and {n(data.excludedAccounts)} teacher or internal{' '}
              {data.excludedAccounts === 1 ? 'account' : 'accounts'}.
            </p>
          )}
        </>
      )}
    </>
  );
}
